import { useEffect, useState, useRef } from 'react';
import { API_BASE_URL } from '../config';

const DeactivateModal = ({ token, siteId, onClose, onSuccess }: { token: string, siteId: string, onClose: () => void, onSuccess: () => void }) => {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    if (modalRef.current) {
      modalRef.current.focus();
    }
    return () => {
      if (previousFocusRef.current) previousFocusRef.current.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div 
        ref={modalRef}
        className="bg-[var(--background3)] border border-[var(--border1)] rounded-lg shadow-xl w-full max-w-sm p-6 relative outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deactivate-title"
        tabIndex={-1}
      >
        <h3 id="deactivate-title" className="text-[16px] md:text-[17px] font-bold text-[var(--text1)] mb-2">Deactivate Background Sync?</h3>
        <p className="text-[var(--text2)] text-[13px] md:text-[14px] mb-6 leading-relaxed">
          Are you sure you want to deactivate background sync? This will remove all your saved schedules for this site.
        </p>
        <div className="flex gap-3 justify-end">
          <button 
            disabled={isDeactivating}
            onClick={onClose}
            className="px-4 py-1.5 rounded text-[13px] md:text-[14px] font-medium text-[var(--text2)] hover:text-[var(--text1)] bg-[var(--background3)] hover:bg-[#444] border border-[#444] transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            disabled={isDeactivating}
            onClick={async () => {
              setIsDeactivating(true);
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 15000);
              try {
                const res = await fetch(`${API_BASE_URL}/api/config/deactivate-sync`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ siteId }),
                  signal: controller.signal
                });
                clearTimeout(timeoutId);
                if (res.ok) onSuccess();
                else alert('Failed to deactivate sync.');
              } catch (e) {
                alert('Network error while deactivating sync.');
              } finally {
                setIsDeactivating(false);
              }
            }}
            className="px-4 py-1.5 rounded text-[13px] md:text-[14px] font-medium text-white bg-[#ef4444] hover:bg-[#dc2626] transition shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isDeactivating ? 'Deactivating...' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface SettingsProps {
  token: string;
  siteId: string | null;
  siteName: string | null;
  workspaceId?: string | null;
  planType: string;
  unlockedSiteId?: string | null;
  onUpgradeClick: () => void;
  onLogout?: () => void;
  userProfile?: { installations?: any[], isPremium: boolean, freePlanCollectionId?: string | null, freeItemsUsed?: number, isPremiumPurchaser?: boolean, invitedEmails?: string[] } | null;
}

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative flex items-center ml-1" tabIndex={0} onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.currentTarget.classList.toggle('group-focus');
    }
  }}>
    <svg tabIndex={0} role="tooltip" aria-label={text} className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 focus:outline-none focus:text-slate-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block group-focus-within:block w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10 text-center whitespace-normal pointer-events-none">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

export default function Settings({ token, siteId, siteName, workspaceId, planType, unlockedSiteId, onUpgradeClick, onLogout, userProfile }: SettingsProps) {
  const isPersonalOnActiveSite = planType === 'PERSONAL' && unlockedSiteId === siteId;
  const effectivePlanType = planType === 'UNLIMITED' ? 'UNLIMITED' : isPersonalOnActiveSite ? 'PERSONAL' : 'FREE';

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [stats, setStats] = useState({
    id: '-',
    collectionsAudited: 0,
    totalSiteCollections: 0,
    totalRules: 0,
    backgroundSync: false
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedList, setInvitedList] = useState<string[]>(userProfile?.invitedEmails || []);
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    if (userProfile?.invitedEmails) {
      setInvitedList(userProfile.invitedEmails);
    }
  }, [userProfile?.invitedEmails]);

  const handleInvite = async (action: 'add' | 'remove', email: string) => {
    if (!email) return;
    setIsInviting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email, action })
      });
      const data = await res.json();
      if (data.success) {
        setInvitedList(data.invitedEmails);
        setInviteEmail('');
      } else {
        alert(data.error || 'Failed to update invite.');
      }
    } catch (e) {
      alert('Network error.');
    } finally {
      setIsInviting(false);
    }
  };

  const fetchStats = () => {
    fetch(`${API_BASE_URL}/api/config/stats${siteId ? `?siteId=${siteId}` : ''}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(async res => {
        if (res.status === 401 && onLogout) {
          onLogout();
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data && !data.error) setStats(data);
      })
      .catch(err => console.error('Error fetching stats:', err));
  };

  useEffect(() => {
    fetchStats();
  }, [token, siteId]);

  if (!siteId) {
    return (
      <div className="p-6 max-w-2xl mx-auto flex flex-col items-center justify-center h-full text-center relative">
        {onLogout && (
          <button 
            onClick={onLogout}
            className="absolute top-0 right-0 text-[12px] md:text-[13px] bg-[var(--background2)] hover:bg-[var(--background3)] text-[var(--text3)] hover:text-[var(--text1)] px-3 py-1.5 rounded transition-colors border border-[var(--border1)]"
          >
            Log Out
          </button>
        )}
        <h2 className="text-xl font-semibold text-[var(--text1)] mb-2">Settings</h2>
        <p className="text-[var(--textInactive)] mb-6">Select a project to view its settings.</p>
        
        <div className="bg-[var(--background2)] border border-[var(--border1)] rounded-lg p-6 max-w-md w-full">
          <h3 className="text-[16px] md:text-[17px] font-semibold text-[var(--text1)] mb-2">Connect a Webflow Workspace</h3>
          <p className="text-[13px] md:text-[14px] text-[var(--text2)] mb-6 leading-relaxed">
            Connect your Webflow account to start auditing your sites and collections. You can connect multiple workspaces to manage them from a single dashboard.
          </p>
          <button 
            onClick={() => window.location.href = `${API_BASE_URL}/api/auth/install?token=${token}`}
            className="w-full bg-[#146EF5] hover:bg-[#0E54C2] text-white font-medium py-2.5 px-4 rounded transition duration-200 shadow-sm flex justify-center items-center gap-2 text-[14px] md:text-[15px]"
          >
            Connect Webflow
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 max-w-3xl mx-auto w-full relative pt-2">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-[var(--text1)]">Settings</h2>
          <p className="text-[14px] md:text-[15px] text-[var(--textInactive)] mt-1">Manage your app preferences and subscription.</p>
        </div>
        {onLogout && (
          <button 
            onClick={onLogout}
            className="text-[12px] md:text-[13px] bg-[var(--background2)] hover:bg-[var(--background3)] text-[var(--text3)] hover:text-[var(--text1)] px-3 py-1.5 rounded transition-colors border border-[var(--border1)]"
          >
            Log Out
          </button>
        )}
      </div>



      {/* Plans Section */}
      <div className="pt-2">
        <h3 className="text-[14px] md:text-[15px] font-semibold text-[var(--text1)] mb-3">Plans</h3>
        <div className="flex gap-4">

          {/* Free Plan */}
          <div className="flex-1 bg-[var(--background2)] border border-[var(--border1)] rounded-md p-5 flex flex-col justify-between">
            <div>
              <h4 className="text-[15px] md:text-[16px] font-semibold text-[var(--text1)] mb-1">Free</h4>
              <div className="text-[14px] md:text-[15px] font-medium text-[var(--text1)] mb-3">$0</div>
              <p className="text-[13px] md:text-[14px] text-[var(--text2)] mb-4 leading-relaxed">
                Run essential health checks. Perfect for trying out the auditor on smaller Webflow projects.
              </p>
              <div className="text-[13px] md:text-[14px] text-[var(--textInactive)] mb-3 font-medium">Features</div>
              <ul className="space-y-2 mb-6">
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Scan up to 5 items globally
                </li>
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Manual on-demand sync
                </li>
              </ul>
            </div>
            <button className="w-full py-2 px-4 rounded border border-[#444] text-[var(--text3)] text-[13px] md:text-[14px] font-medium bg-[var(--background1)] cursor-not-allowed">
              {effectivePlanType === 'FREE' ? 'Current plan' : 'Included'}
            </button>
          </div>

          {/* Personal Plan */}
          <div className="flex-1 bg-[var(--background2)] border border-[var(--border1)] rounded-md p-5 flex flex-col justify-between relative overflow-hidden">
            {effectivePlanType === 'PERSONAL' && <div className="absolute top-0 left-0 w-full h-[2px] bg-[#10b981]"></div>}
            <div>
              <h4 className="text-[15px] md:text-[16px] font-semibold text-[var(--text1)] mb-1">Personal</h4>
              <div className="text-[14px] md:text-[15px] font-medium text-[var(--text1)] mb-3">$9/mo or $86/yr</div>
              <p className="text-[13px] md:text-[14px] text-[var(--text2)] mb-4 leading-relaxed">
                Designed for single site owners who want full access to the auditor.
              </p>
              <div className="text-[13px] md:text-[14px] text-[var(--textInactive)] mb-3 font-medium">Features</div>
              <ul className="space-y-2 mb-6">
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Unlimited items scanned
                </li>
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  1 Webflow Site inside workspace
                </li>
              </ul>
            </div>
            {effectivePlanType === 'PERSONAL' ? (
              <div className="flex flex-col gap-1.5">
                <button 
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_BASE_URL}/api/lemonsqueezy/portal?workspaceId=${workspaceId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                      const data = await res.json();
                      if (data.url) window.open(data.url, '_blank');
                      else alert('Could not open portal.');
                    } catch (e) {}
                  }}
                  className="w-full py-2 px-4 rounded bg-[#10b981] text-white border border-[#059669] text-[13px] md:text-[14px] font-medium hover:bg-[#059669] transition-colors shadow-sm"
                >
                  Manage Subscription
                </button>
                <div className="text-[10px] md:text-[11px] text-[var(--text3)] text-center">
                  Please reload the app after making changes.
                </div>
              </div>
            ) : effectivePlanType === 'UNLIMITED' ? (
              <button className="w-full py-2 px-4 rounded border border-[#444] text-[var(--text3)] text-[13px] md:text-[14px] font-medium bg-[var(--background1)] cursor-not-allowed">
                Included
              </button>
            ) : (
              <button onClick={onUpgradeClick} className="w-full py-2 px-4 rounded bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[13px] md:text-[14px] font-medium transition-colors shadow-sm">
                Upgrade
              </button>
            )}
          </div>

          {/* Unlimited Plan */}
          <div className="flex-1 bg-[var(--background2)] border border-[var(--border1)] rounded-md p-5 flex flex-col justify-between relative overflow-hidden">
            {effectivePlanType === 'UNLIMITED' && <div className="absolute top-0 left-0 w-full h-[2px] bg-[#10b981]"></div>}
            <div>
              <h4 className="text-[15px] md:text-[16px] font-semibold text-[var(--text1)] mb-1">Unlimited</h4>
              <div className="text-[14px] md:text-[15px] font-medium text-[var(--text1)] mb-3 group relative w-fit cursor-default">
                $29/mo or $278/yr
              </div>
              <p className="text-[13px] md:text-[14px] text-[var(--text2)] mb-4 leading-relaxed">
                For professional agencies and freelancers managing multiple client sites.
              </p>
              <div className="text-[13px] md:text-[14px] text-[var(--textInactive)] mb-3 font-medium">Features</div>
              <ul className="space-y-2 mb-6">
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Unlimited items scanned
                </li>
                <li className="flex gap-2 text-[13px] md:text-[14px] text-[var(--text2)]">
                  <svg className="w-4 h-4 text-[#4ade80] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Unlimited Webflow Sites
                </li>
              </ul>
            </div>
            {planType === 'UNLIMITED' ? (
              <div className="flex flex-col gap-1.5">
                <button 
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_BASE_URL}/api/lemonsqueezy/portal?workspaceId=${workspaceId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                      const data = await res.json();
                      if (data.url) window.open(data.url, '_blank');
                      else alert('Could not open portal.');
                    } catch (e) {}
                  }}
                  className="w-full py-2 px-4 rounded bg-[#10b981] text-white border border-[#059669] text-[13px] md:text-[14px] font-medium hover:bg-[#059669] transition-colors shadow-sm"
                >
                  Manage Subscription
                </button>
                <div className="text-[10px] md:text-[11px] text-[var(--text3)] text-center">
                  Please reload the app after making changes.
                </div>
              </div>
            ) : (
              <button onClick={onUpgradeClick} className="w-full py-2 px-4 rounded bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[13px] md:text-[14px] font-medium transition-colors shadow-sm">
                Upgrade
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Project Section */}
      <div className="pt-2">
        <h3 className="text-[14px] md:text-[15px] font-semibold text-[var(--text1)] mb-3">Project</h3>
        <div className="border border-[var(--border1)] rounded-sm overflow-hidden text-[13px] md:text-[14px] text-[var(--text2)]">
          <div className="flex border-b border-[var(--border1)]">
            <div className="w-1/3 p-3 font-medium bg-[var(--background2)]">Name</div>
            <div className="w-2/3 p-3 bg-[var(--background1)]">{siteName || '-'}</div>
          </div>
          <div className="flex border-b border-[var(--border1)]">
            <div className="w-1/3 p-3 font-medium bg-[var(--background2)]">ID</div>
            <div className="w-2/3 p-3 bg-[var(--background1)]">{siteId}</div>
          </div>
          <div className="flex border-b border-[var(--border1)]">
            <div className="w-1/3 p-3 font-medium bg-[var(--background2)] flex items-center">
              Collections Monitored
              <Tooltip text="Number of collections configured for automated background health checks." />
            </div>
            <div className="w-2/3 p-3 bg-[var(--background1)]">{stats.collectionsAudited}{stats.totalSiteCollections ? `/${stats.totalSiteCollections}` : ''}</div>
          </div>
          <div className="flex border-b border-[var(--border1)]">
            <div className="w-1/3 p-3 font-medium bg-[var(--background2)]">Total Rules</div>
            <div className="w-2/3 p-3 bg-[var(--background1)]">{stats.totalRules}</div>
          </div>
          <div className="flex items-center border-b border-[var(--border1)]">
            <div className="w-1/3 p-3 font-medium bg-[var(--background2)] h-full flex items-center">Background Sync</div>
            <div className="w-2/3 p-2 bg-[var(--background1)] flex justify-between items-center">
              {stats.backgroundSync ? (
                <div className="flex items-center gap-2 text-[#4ade80]">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Active
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[var(--text3)]">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Inactive
                </div>
              )}
              {stats.backgroundSync && (
                <button 
                  onClick={() => setShowDeactivateModal(true)}
                  className="bg-transparent hover:bg-red-50 text-[#ef4444] border border-[#ef4444] hover:border-[#dc2626] text-[12px] md:text-[13px] font-medium py-1.5 px-3 rounded transition duration-150"
                >
                  Deactivate
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Background Sync Modal */}
      {showDeactivateModal && (
        <DeactivateModal 
          token={token} 
          siteId={siteId!} 
          onClose={() => setShowDeactivateModal(false)} 
          onSuccess={() => {
            setShowDeactivateModal(false);
            fetchStats();
          }} 
        />
      )}

      {/* How it works Section */}
      <div className="pt-2">
        <h3 className="text-[14px] md:text-[15px] font-semibold text-[var(--text1)] mb-3">How it works</h3>
        <ul className="list-disc pl-5 space-y-2 text-[13px] md:text-[14px] text-[var(--text2)]">
          <li>CMS Auditor is paired with this Webflow workspace.</li>
          <li>Plans are subscriptions billed monthly or yearly.</li>
          <li>The Personal Plan locks to the Webflow site you upgrade from.</li>
        </ul>
      </div>

      {/* Connected Workspaces Section */}
      <div className="pt-6 pb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[14px] md:text-[15px] font-semibold text-[var(--text1)]">Connected Workspaces</h3>
          <button 
            onClick={() => window.location.href = `${API_BASE_URL}/api/auth/install?token=${token}`}
            className="text-[12px] md:text-[13px] bg-[var(--background2)] hover:bg-[var(--background3)] border border-[var(--border1)] text-[var(--text1)] py-1.5 px-3 rounded transition-colors"
          >
            + Connect New
          </button>
        </div>
        
        {userProfile?.installations && userProfile.installations.length > 0 ? (
          <div className="space-y-3">
            {Array.from(new Map(userProfile.installations.filter((i: any) => i.workspace).map((inst: any) => [inst.workspace.id, inst.workspace])).values()).map((ws: any, idx: number) => {
              const isWsPremium = ws?.planType === 'UNLIMITED' || ws?.planType === 'PERSONAL';
              
              return (
                <div key={ws.id || idx} className="bg-[var(--background2)] border border-[var(--border1)] rounded p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[14px] md:text-[15px] font-medium text-[var(--text1)]">Workspace {ws?.id?.substring(0, 8) || 'Unknown'}</span>
                      {isWsPremium ? (
                        <span className="text-[10px] md:text-[11px] bg-[#10b981]/20 text-[#10b981] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider">Premium</span>
                      ) : (
                        <span className="text-[10px] md:text-[11px] bg-[#666]/20 text-[var(--text3)] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider">Free</span>
                      )}
                    </div>
                    <div className="text-[12px] md:text-[13px] text-[var(--text2)]">
                      Plan: <span className="font-medium text-[var(--text1)]">{ws?.planType || 'FREE'}</span>
                      {ws?.planType === 'PERSONAL' && ws?.unlockedSiteId && (
                        <span className="ml-1 text-[11px] md:text-[12px] text-[var(--text3)]">(Locked to Site: {ws.unlockedSiteId.substring(0, 8)})</span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <button 
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_BASE_URL}/api/lemonsqueezy/portal?workspaceId=${ws.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
                          const data = await res.json();
                          if (data.url) window.open(data.url, '_blank');
                          else alert('Could not open portal.');
                        } catch (e) {}
                      }}
                      className="text-[12px] md:text-[13px] bg-[#146EF5]/10 hover:bg-[#146EF5]/20 text-[#146EF5] font-medium py-1.5 px-3 rounded transition-colors"
                    >
                      Manage Plan
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-[13px] md:text-[14px] text-[var(--text3)] bg-[var(--background2)] border border-[var(--border1)] rounded p-4 text-center">
            No workspaces connected yet.
          </div>
        )}
      </div>

      {/* Premium Access Management Section */}
      {userProfile?.isPremiumPurchaser && (
        <div className="pt-2 pb-6">
          <h3 className="text-[14px] md:text-[15px] font-semibold text-[var(--text1)] mb-3">Premium Access Management</h3>
          <div className="bg-[var(--background2)] border border-[var(--border1)] rounded-md p-5">
            <p className="text-[13px] md:text-[14px] text-[var(--text2)] mb-4 leading-relaxed">
              You are the premium purchaser for this workspace. You can invite other users (like your clients) to access the premium features on this workspace by adding their Webflow email address here.
            </p>
            <div className="flex gap-3 mb-6">
              <input 
                type="email" 
                value={inviteEmail} 
                onChange={(e) => setInviteEmail(e.target.value)} 
                placeholder="client@example.com" 
                className="flex-1 px-3 py-2 border border-[var(--border1)] rounded bg-[var(--background1)] text-[var(--text1)] focus:outline-none focus:border-[#146EF5]"
                disabled={isInviting}
              />
              <button 
                onClick={() => handleInvite('add', inviteEmail)}
                disabled={isInviting || !inviteEmail}
                className="px-4 py-2 bg-[#146EF5] text-white rounded font-medium hover:bg-[#0E54C2] transition-colors disabled:opacity-50"
              >
                {isInviting ? 'Adding...' : 'Add User'}
              </button>
            </div>
            
            {invitedList.length > 0 ? (
              <div className="border border-[var(--border1)] rounded overflow-hidden">
                {invitedList.map((email, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 border-b border-[var(--border1)] last:border-b-0 bg-[var(--background1)]">
                    <span className="text-[13px] md:text-[14px] text-[var(--text1)]">{email}</span>
                    <button 
                      onClick={() => handleInvite('remove', email)}
                      disabled={isInviting}
                      className="text-[12px] md:text-[13px] text-red-500 hover:text-red-600 font-medium disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[13px] md:text-[14px] text-[var(--textInactive)]">
                No users have been invited yet.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  )
}
